import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useLocalState } from "../../../hooks";
import Api from "../../../store/api";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const parseNoteText = (rawNote: any) => {
  if (!rawNote) return "";
  if (typeof rawNote !== "string") return String(rawNote);

  try {
    const parsed = JSON.parse(rawNote);
    if (Array.isArray(parsed)) {
      const textParts = parsed
        .map((item: any) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") return item.text || item.note || "";
          return "";
        })
        .filter(Boolean);
      return textParts.join("\n");
    }
    if (parsed && typeof parsed === "object") {
      return parsed.text || parsed.note || rawNote;
    }
  } catch (error) {
    return rawNote;
  }

  return rawNote;
};

export default function NotesList() {
  const navigate = useNavigate();
  const authUser = useSelector((state: any) => state.auth?.user);
  const defaultState = {
    loading: false,
    notes: [],
    query: "",
    errorMessage: "",
    openingProfileId: 0,
    deletingProfileId: 0,
  };
  const [LS, setLS] = useLocalState(defaultState);

  useEffect(() => {
    const loadNotes = async () => {
      if (!authUser?.id) return;
      setLS({ loading: true, errorMessage: "" });
      try {
        const response = await Api.getMyNotes(authUser.id);
        const notes = response?.data?.data || [];
        const notesWithName = await Promise.all(
          notes.map(async (item: any) => {
            try {
              const profile = await Api.getProfileDetails(item.toId);
              const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ");
              return {
                ...item,
                profileName: fullName || "",
                profileMemberId: profile?.memberId || "",
              };
            } catch (error) {
              return {
                ...item,
                profileName: "",
                profileMemberId: "",
              };
            }
          })
        );
        setLS({ loading: false, notes: notesWithName });
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to load notes.";
        setLS({ loading: false, errorMessage });
      }
    };

    loadNotes();
  }, [authUser?.id]);

  const openProfile = async (toId: number) => {
    if (!toId) return;
    setLS({ openingProfileId: toId });
    try {
      const profile = await Api.getProfileDetails(toId);
      const result: any = await Api.compressImage(profile.img, 300, 300);
      const selectedProfile = { ...profile, id: profile.userId || toId, img: result?.error ? profile.img : result.imageUri };
      sessionStorage.setItem("selectedProfile", JSON.stringify(selectedProfile));
      navigate("/user/view-profile");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to open profile.";
      setLS({ errorMessage });
    } finally {
      setLS({ openingProfileId: 0 });
    }
  };

  const deleteNote = async (toId: number) => {
    if (!toId || !authUser?.id) return;
    const isConfirmed = window.confirm("Delete this note?");
    if (!isConfirmed) return;

    setLS({ deletingProfileId: toId, errorMessage: "" });
    try {
      await Api.deleteMyNote(authUser.id, toId);
      setLS({ notes: LS.notes.filter((item: any) => item.toId !== toId), deletingProfileId: 0 });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete note.";
      setLS({ deletingProfileId: 0, errorMessage });
    }
  };

  const filteredNotes = LS.notes.filter((item: any) => {
    const query = (LS.query || "").trim().toLowerCase();
    if (!query) return true;
    const profileId = String(item?.toId || "").toLowerCase();
    const profileName = String(item?.profileName || "").toLowerCase();
    const profileMemberId = String(item?.profileMemberId || "").toLowerCase();
    const note = parseNoteText(item?.note).toLowerCase();
    return profileId.includes(query) || profileName.includes(query) || profileMemberId.includes(query) || note.includes(query);
  });

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          My Notes
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Notes you added while viewing profiles.
        </Typography>

        <TextField
          fullWidth
          size="small"
          value={LS.query}
          onChange={(e) => setLS({ query: e.target.value })}
          placeholder="Search by Profile ID or note text"
          InputProps={{
            endAdornment: LS.query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setLS({ query: "" })} aria-label="Clear search">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        {LS.loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!LS.loading && LS.errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {LS.errorMessage}
          </Alert>
        )}

        {!LS.loading && !LS.errorMessage && !LS.notes.length && (
          <Typography color="textSecondary">No notes found.</Typography>
        )}

        {!LS.loading && !LS.errorMessage && !!LS.notes.length && !filteredNotes.length && (
          <Typography color="textSecondary">No notes match your search.</Typography>
        )}

        {!LS.loading && !!filteredNotes.length && (
          <List disablePadding>
            {filteredNotes.map((item: any, index: number) => (
              <Box key={item.id || `${item.toId}-${index}`}>
                <ListItem
                  disableGutters
                  secondaryAction={
                    <Box display="flex" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openProfile(item.toId)}
                        disabled={LS.openingProfileId === item.toId || LS.deletingProfileId === item.toId}
                      >
                        {LS.openingProfileId === item.toId ? "Opening..." : "Open Profile"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => deleteNote(item.toId)}
                        disabled={LS.deletingProfileId === item.toId || LS.openingProfileId === item.toId}
                      >
                        {LS.deletingProfileId === item.toId ? "Deleting..." : "Delete"}
                      </Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`${item.profileMemberId || `Profile ID: ${item.toId}`}${item.profileName ? ` - ${item.profileName}` : ""}`}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="textPrimary" sx={{ display: "block", mb: 0.5 }}>
                          {parseNoteText(item.note)}
                        </Typography>
                        <Typography component="span" variant="caption" color="textSecondary">
                          {formatDate(item.updated_at || item.created_at)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < filteredNotes.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
